// WS-72 step 4: still things stay still (engine-generic; resortMap.still.set(false) in a page = every matrix every frame).
// Frames are identical to the old path; only three.js book-keeping that gave the same answer every frame goes away:
//  1. Matrices. The scene root and every object that has not moved for `wait` frames after it was added stop
//     recomputing their matrices each frame (matrixAutoUpdate = false). Never frozen: lights and their targets, the
//     camera, bones and skinned meshes, batched meshes, points / lines / sprites, hidden sources other code reads
//     (layers.mask 0: the instancer's skeletons and sleds), animals (userData.kind), the sky (userData.sky), moving
//     shadow casters (userData.shadowLive) and anything marked userData.live, each with its whole subtree.
//     Moving a frozen object: set its position / rotation / scale, then call obj.updateMatrix() (three.js's own
//     contract; resortMap.still.touch(obj) does the same). A watchdog compares a frozen object's pose with its matrix
//     (`sweep` objects a frame; all of them every frame in the editors, `full`) and wakes one that moved without that
//     call: it is live from then on and listed in info().woke.
//  2. (gone: WebGPURenderer caches a render pipeline per object, material and state, so a material shared by plain,
//     instanced, batched and skinned meshes needs no per-kind copy.)
//  3. The invisible pick meshes (`picks`) are taken out of the draw walk (visible = false; the ray casts ignore it).
import * as THREE from 'three/webgpu';

const same = (a, b) => { for (let i = 0; i < 16; i++) if (a[i] !== b[i]) return false; return true; };

/** @param {THREE.Scene} scene @param {THREE.Camera} camera @param {{ picks?: THREE.Object3D[], full?: boolean, wait?: number, sweep?: number }} [o] */
export function createStill(scene, camera, { picks = [], full = false, wait = 90, sweep = 32 } = {}) {
  let on = false, frame = 0, top = '', cur = 0, seen, wait0, frozen, hid;
  const m4 = new THREE.Matrix4(), woke = [], stats = { settles: 0 };
  const pose = (o) => m4.compose(o.position, o.quaternion, o.scale).elements;
  const liveRoot = (o) => o.isLight || o.isCamera || o.isBone || o.isSkinnedMesh || o.isBatchedMesh || o.isPoints || o.isLine || o.isSprite || o.layers.mask === 0 || o.userData.kind || o.userData.sky || o.userData.shadowLive || o.userData.live;
  function settle() {
    stats.settles++;
    const targets = new Set();
    scene.traverse((o) => { if (o.target?.isObject3D) targets.add(o.target); });
    (function walk(o) { // matrix candidates, watched for `wait` frames before they freeze; true = keep live
      if (liveRoot(o) || targets.has(o)) return true;
      let live = false;
      for (const c of o.children) live = walk(c) || live; // the parent of a live thing stays live (e.g. the ride's guide, who carries hidden sources)
      if (!live && !seen.has(o) && o.matrixAutoUpdate) { seen.add(o); wait0.set(o, { f: frame, m: pose(o).slice() }); }
      return live;
    })(scene);
    for (const p of picks) if (p.visible && p.isMesh && !p.children.length && p.material?.visible === false) { p.visible = false; hid.push(p); }
  }
  function set(v) {
    if (v && !on) { scene.matrixAutoUpdate = false; seen = new WeakSet(); wait0 = new Map(); frozen = []; hid = []; top = ''; }
    if (!v && on) {
      scene.matrixAutoUpdate = true;
      for (const o of frozen) o.matrixAutoUpdate = true;
      for (const o of hid) o.visible = true;
    }
    on = !!v;
  }
  set(true);
  return {
    /** once a frame, after every animation step and before the render */
    frame() {
      if (!on) return;
      frame++;
      const t = `${scene.children.length}|${scene.children.at(-1)?.id}`;
      if (t !== top || (frame & 1023) === 0) { top = t; settle(); } // new objects (late models, a rebuilt fence), and now and then
      for (const [o, w] of wait0) { // still for `wait` frames: frozen; moved: it stays live
        if (!same(pose(o), w.m)) wait0.delete(o);
        else if (frame - w.f >= wait) { // (a camera follower such as the sky dome or the clouds stays live)
          wait0.delete(o);
          if (o.parent && o.matrixAutoUpdate && !o.position.equals(camera.position)) { o.updateMatrix(); o.matrixAutoUpdate = false; frozen.push(o); }
        }
      }
      for (let n = full ? frozen.length : Math.min(sweep, frozen.length); n > 0; n--) { // the watchdog
        cur = (cur + 1) % frozen.length;
        const o = frozen[cur];
        if (o.matrixAutoUpdate || !o.parent) { o.matrixAutoUpdate = true; frozen[cur] = frozen.at(-1); frozen.pop(); continue; } // made live by its owner, or gone
        if (!same(pose(o), o.matrix.elements)) { o.matrixAutoUpdate = true; o.updateMatrix(); if (woke.length < 24) woke.push(`${o.name || o.type}#${o.id}`); }
      }
    },
    touch(o) { o.updateMatrix(); }, // after moving a frozen object (the same as o.updateMatrix())
    set, // QA: false = the old path in this page, true = still again (objects freeze after `wait` frames)
    info: () => ({ on, frozen: frozen.length, waiting: wait0.size, twins: 0, swaps: 0, depth: 0, hidden: hid.length, woke: woke.slice(), ...stats }), // (twins / swaps / depth: WS-75, always 0)
  };
}
